/** Column inference: find date / description / amount (or debit+credit) columns. */

import { parseAmount } from './amounts';
import { parseDate } from './dates';

export interface ColumnIndexes {
  date: number;
  desc: number;
  amount: number;
  debit: number;
  credit: number;
}

const DATE_HEADERS = /(^|[^a-z])(date|posted|time|when)([^a-z]|$)/i;
const DESC_HEADERS = /desc|merchant|name|payee|memo|detail|narrative|reference|transaction/i;
const AMOUNT_HEADERS = /^amount$|amount|value|charge|total/i;
const DEBIT_HEADERS = /debit|withdrawal|money.?out|paid.?out/i;
const CREDIT_HEADERS = /credit|deposit|money.?in|paid.?in/i;

export function inferColumns(headers: string[], sampleRows: string[][]): ColumnIndexes {
  const idx: ColumnIndexes = { date: -1, desc: -1, amount: -1, debit: -1, credit: -1 };

  headers.forEach((h, i) => {
    const t = h.trim();
    if (idx.date === -1 && DATE_HEADERS.test(t)) idx.date = i;
    if (idx.debit === -1 && DEBIT_HEADERS.test(t)) idx.debit = i;
    else if (idx.credit === -1 && CREDIT_HEADERS.test(t)) idx.credit = i;
    else if (idx.amount === -1 && AMOUNT_HEADERS.test(t)) idx.amount = i;
  });
  headers.forEach((h, i) => {
    if (idx.desc === -1 && i !== idx.date && i !== idx.amount && DESC_HEADERS.test(h.trim())) {
      idx.desc = i;
    }
  });

  // Fall back to content sniffing.
  const cols = headers.length;
  if (idx.date === -1) {
    for (let i = 0; i < cols; i++) {
      if (sampleRows.filter((r) => parseDate(r[i])).length >= sampleRows.length * 0.8) {
        idx.date = i;
        break;
      }
    }
  }
  if (idx.amount === -1 && idx.debit === -1) {
    for (let i = cols - 1; i >= 0; i--) {
      if (i === idx.date) continue;
      if (sampleRows.filter((r) => parseAmount(r[i]) != null).length >= sampleRows.length * 0.8) {
        idx.amount = i;
        break;
      }
    }
  }
  if (idx.desc === -1) {
    let best = -1;
    let bestLen = 0;
    for (let i = 0; i < cols; i++) {
      if (i === idx.date || i === idx.amount || i === idx.debit || i === idx.credit) continue;
      const avgLen = sampleRows.reduce((a, r) => a + String(r[i] ?? '').length, 0) / sampleRows.length;
      const nonNumeric = sampleRows.filter(
        (r) => parseAmount(r[i]) == null || /[a-z]/i.test(r[i] ?? ''),
      ).length;
      if (nonNumeric >= sampleRows.length * 0.6 && avgLen > bestLen) {
        best = i;
        bestLen = avgLen;
      }
    }
    idx.desc = best;
  }
  return idx;
}
